import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUpcomingGames } from '../../api/games';
import { getAllTeamData } from '../../api/teams';
import { unlockGamePrediction } from '../../api/credits';
import { getUserAccess } from '../../api/user';
import GameCard from '../../components/GameCard';
import Grid from '../../components/Layouts/Grid';
import LoadingSpinner from '../../components/LoadingSpinner';
import GameSearchFilter from '../../components/GameSearchFilter';
import BackToTop from '../../components/BackToTop';
import GameCardsTour from '../../components/Tours/GameCardsTour';
import { attachTeamDetails, filterGames, gameMissingPrediction, getMissingPredictionMessage } from '../../components/GameCard/utils';
import { BRANDING } from '../../config/branding';
import { setTeams, setSport } from '../../store/slices/appSlice';
import { setAccess } from '../../store/slices/accessSlice';
import { PageWrap, FooterColorBody, NoDataWrap, NoDataIcon, NoDataTitle, NoDataMessage, ErrorWrap, NoticeWrap, PropsBar, PropsBarBtn, PropsBarLabel } from './styles';

const Sports = () => {
  const { sport } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const hasPropsAccess = useSelector(state => Boolean(state.access.paid) && Boolean(state.access.hasPropsUpgrade));

  const [games, setGames]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [value, setValue]       = useState('');
  const [sort, setSort]         = useState('Time');
  const [filters, setFilters]   = useState([]);
  const [activeDate, setActiveDate] = useState(moment().toISOString());

  // Sync sport to Redux so window components (PowerRank, etc.) can read it
  useEffect(() => {
    dispatch(setSport(sport));
  }, [sport, dispatch]);

  // ── Fetch games whenever the sport changes ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setValue('');
    setSort('Time');
    setFilters([]);

    Promise.all([getUpcomingGames(sport), getAllTeamData(sport)])
      .then(([gamesRes, teamsRes]) => {
        if (!cancelled) {
          const gameList = Array.isArray(gamesRes) ? gamesRes : gamesRes?.data || [];
          const teamList = Array.isArray(teamsRes) ? teamsRes : teamsRes?.data || [];
          const enrichedGames = attachTeamDetails(gameList, teamList);
          setGames(enrichedGames);
          // Sync activeDate to first available game date
          if (enrichedGames.length) {
            const earliest = enrichedGames.reduce((prev, curr) => (prev.dateTime < curr.dateTime ? prev : curr));
            setActiveDate(earliest.dateTime);
          } else {
            setActiveDate(moment().toISOString());
          }
          dispatch(setTeams(teamList));
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Failed to load game data.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [sport]);

  // ── Filter + sort using LPA's exact filterGames util ───────────────────
  const processGames = useCallback(() => {
    let result = filterGames(games, value, filters, activeDate);

    if (sort === 'PW Rating') {
      const useSpread = sport === 'mlb' || sport === 'nhl';
      result = [...result].sort((a, b) => {
        const ca = useSpread ? a.prediction?.spreadConfidence : a.prediction?.confidence;
        const cb = useSpread ? b.prediction?.spreadConfidence : b.prediction?.confidence;
        if (ca != null && cb != null) return cb - ca;
        return 0;
      });
    }

    return result;
  }, [games, activeDate, value, filters, sort, sport]);

  const filtered = processGames();
  const showMissingPredictionNotice = !loading && !error && filterGames(games, '', [], activeDate).some(gameMissingPrediction);

  const renderContent = () => {
    if (loading) return <LoadingSpinner />;
    if (error)   return <ErrorWrap>{error}</ErrorWrap>;

    if (!filtered.length) {
      return (
        <NoDataWrap>
          <NoDataIcon className="fas fa-table-cells-large" />
          <NoDataTitle>No Games Found</NoDataTitle>
          <NoDataMessage>
            {games.length
              ? 'No games match your current filters. Try adjusting the date or search.'
              : `No upcoming ${String(sport).toUpperCase()} games are available right now. Check back later.`}
          </NoDataMessage>
        </NoDataWrap>
      );
    }

    return filtered.map(game => (
      <GameCard
        key={game.gameId || `${game.awayTeam?.teamName}-${game.homeTeam?.teamName}-${game.dateTime}`}
        game={game}
        sport={sport}
        hasPropsAccess={hasPropsAccess}
        onUnlockPrediction={async selectedGame => {
          try {
            await unlockGamePrediction({ sport, gameId: selectedGame?.gameId });

            const access = await getUserAccess();
            dispatch(setAccess(access));

            const [refreshedGames, refreshedTeams] = await Promise.all([
              getUpcomingGames(sport),
              getAllTeamData(sport)
            ]);
            const gameList = Array.isArray(refreshedGames) ? refreshedGames : refreshedGames?.data || [];
            const teamList = Array.isArray(refreshedTeams) ? refreshedTeams : refreshedTeams?.data || [];
            setGames(attachTeamDetails(gameList, teamList));
          } catch (unlockError) {
            setError(unlockError.message || 'Unable to unlock this prediction.');
          }
        }}
      />
    ));
  };

  return (
    <PageWrap $fillViewport={loading || !filtered.length}>
      <FooterColorBody />
      <Helmet>
        <title>{`${String(sport).toUpperCase()} Game Cards | ${BRANDING.appName}`}</title>
        <meta name="description" content={`${String(sport).toUpperCase()} game cards with predictions, Proven Line, and PW Rating.`} />
      </Helmet>

      <PropsBar>
        <PropsBarLabel>
          <i className={hasPropsAccess ? 'fas fa-user-check' : 'fas fa-lock'} />
          {hasPropsAccess ? 'Props Add-On Active' : 'Props Add-On Not Active'}
        </PropsBarLabel>
        {hasPropsAccess ? (
          <PropsBarBtn onClick={() => history.push(`/props/${sport}`)}>
            <i className="fas fa-external-link-alt" />
            View {String(sport).toUpperCase()} Props
          </PropsBarBtn>
        ) : (
          <PropsBarBtn onClick={() => history.push('/settings?tab=subscription-management')}>
            <i className="fas fa-plus-circle" />
            Add Props Access
          </PropsBarBtn>
        )}
      </PropsBar>

      <GameSearchFilter
        games={games}
        activeDate={activeDate}
        setActiveDate={setActiveDate}
        sort={sort}
        setSort={setSort}
        value={value}
        setValue={setValue}
        filters={filters}
        setFilters={setFilters}
      />

      {showMissingPredictionNotice && (
        <NoticeWrap>
          <i className="fas fa-exclamation-triangle" />
          {getMissingPredictionMessage(sport)}
        </NoticeWrap>
      )}

      <Grid>{renderContent()}</Grid>
      <GameCardsTour hasGames={games.length > 0} />
      <BackToTop />
    </PageWrap>
  );
};

export default Sports;
